/**
 * G10 — in-battle bag orchestration, pure logic. BattleScene renders
 * the picker on the 4-button action surface and owns the display
 * writes; this module owns the rules:
 *
 *   - Usable-in-battle = heal/cure kinds with count > 0. Key items are
 *     field-only (cartridge: no TOWN MAP mid-battle).
 *   - Validation runs against the BATTLE's live model (hpById + sim
 *     status snapshot), never stale runState — a mon poisoned two
 *     turns ago must count as poisoned here.
 *   - A failed check consumes nothing; the caller returns the player
 *     to the picker (cartridge: pick someone else).
 *
 * Consumption ordering (BattleScene enforces): checkBattleUse → sim
 * useItemTurn (its false also consumes nothing) → runState.removeItem
 * — the item is only burned after the sim committed the turn.
 */
import { ITEMS } from '../data/items';
import { CURE_MESSAGE, NO_EFFECT } from '../state/bagUse';
import type { PartyMemberStatus } from './statusVisuals';
import type { UseItemTurnArgs } from './runInteractiveBattle';

export interface BattleBagRow {
  itemId: string;
  name: string;
  count: number;
  desc: string;
}

/** Heal/cure stacks the player holds, ITEMS-registry order (stable
 *  across turns so button positions don't shuffle). */
export function usableBattleItems(
  bag: Readonly<Record<string, number>>,
): BattleBagRow[] {
  const rows: BattleBagRow[] = [];
  for (const def of Object.values(ITEMS)) {
    if (def.effect.kind === 'key') continue;
    const count = bag[def.id] ?? 0;
    if (count <= 0) continue;
    rows.push({ itemId: def.id, name: def.name, count, desc: def.desc });
  }
  return rows;
}

export interface BattleUseTarget {
  displayName: string;
  hpCurrent: number;
  hpMax: number;
  status: PartyMemberStatus;
  fainted: boolean;
}

export type BattleUseCheck =
  | {
      ok: true;
      effect: UseItemTurnArgs['effect'];
      /** Battle-log beats, in order ("Used POTION on PIKACHU!" then
       *  the recovery line). */
      lines: string[];
      /** For heals: the target's post-heal current HP, for the
       *  caller's hpById write. Undefined for cures. */
      healedTo?: number;
    }
  | { ok: false; message: string };

/** Mirror of bagUse's cartridge rules against a battle-model target.
 *  Fainted targets are no-effect (Revives don't exist in this game —
 *  permadeath; the picker shouldn't list them, this is the backstop). */
export function checkBattleUse(
  itemId: string,
  target: BattleUseTarget,
): BattleUseCheck {
  const def = ITEMS[itemId];
  if (!def || def.effect.kind === 'key' || target.fainted) {
    return { ok: false, message: NO_EFFECT };
  }
  const usedLine = `Used ${def.name} on ${target.displayName}!`;

  if (def.effect.kind === 'heal') {
    if (target.hpCurrent >= target.hpMax) {
      return { ok: false, message: NO_EFFECT };
    }
    const healedTo = Math.min(
      target.hpMax,
      target.hpCurrent + def.effect.amount,
    );
    return {
      ok: true,
      effect: { kind: 'heal', amount: def.effect.amount },
      lines: [
        usedLine,
        `${target.displayName} recovered ${healedTo - target.hpCurrent} HP!`,
      ],
      healedTo,
    };
  }

  if (!target.status || !def.effect.cures.includes(target.status)) {
    return { ok: false, message: NO_EFFECT };
  }
  return {
    ok: true,
    effect: { kind: 'cure', cures: [...def.effect.cures] },
    lines: [
      usedLine,
      CURE_MESSAGE[target.status].replace('{NAME}', target.displayName),
    ],
  };
}
